import cookies from 'js-cookie';
import { t } from 'i18next';
import { fitLabelContent } from './labelRender';
// @ts-ignore jsbarcode ships without types in this project
import JsBarcode from 'jsbarcode';
import { buildOrderFolio } from './ordersUtils';
import { getCustomerFullName } from './strings';

export type LabelSize = '4x6' | '4x3' | '3x3' | '2x1';
export const LABEL_SIZE_OPTIONS: LabelSize[] = ['4x6', '4x3', '3x3']; //removed , '2x1' because of sizing and space for content

const labelPresets: Record<
  LabelSize,
  {
    widthIn: number;
    heightIn: number;
    widthMm: number;
    heightMm: number;
    canvasWidth: string;
    canvasHeight: string;
    fontSize: number;
    barcodeHeightIn: number;
  }
> = {
  '4x6': {
    widthIn: 4,
    heightIn: 6,
    widthMm: 101.6, // 4in
    heightMm: 152.4, // 6in
    canvasWidth: '4in',
    canvasHeight: '6in',
    fontSize: 28,
    barcodeHeightIn: 6 * 0.4
  },
  '4x3': {
    widthIn: 4,
    heightIn: 3,
    widthMm: 101.6, // 4in
    heightMm: 76.2, // 3in
    canvasWidth: '4in',
    canvasHeight: '3in',
    fontSize: 22,
    barcodeHeightIn: 3 * 0.4
  },
  '3x3': {
    widthIn: 3,
    heightIn: 3,
    widthMm: 76.2, // 3in
    heightMm: 76.2, // 3in
    canvasWidth: '3in',
    canvasHeight: '3in',
    fontSize: 20,
    barcodeHeightIn: 3 * 0.4
  },
  '2x1': {
    widthIn: 2,
    heightIn: 1,
    widthMm: 50.8, // 2in
    heightMm: 25.4, //1in
    canvasWidth: '2in',
    canvasHeight: '1in',
    fontSize: 16,
    barcodeHeightIn: 1 * 0.4
  }
};

export const escapeLabelHtml = (unsafe: unknown) =>
  String(unsafe ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

export const getLabelPrefs = () => {
  const labelPrinter = cookies.get('labelPrinter') || localStorage.getItem('labelPrinter') || '';
  const labelSize =
    (cookies.get('labelPrinterSize') || localStorage.getItem('labelPrinterSize') || '2x1') as LabelSize;
  return { labelPrinter, labelSize };
};

export const getLabelPreset = (labelSize: LabelSize) => {
  const preset = labelPresets[labelSize] || labelPresets['2x1'];
  return {
    ...preset,
    widthPx: preset.widthIn * 96,
    heightPx: preset.heightIn * 96
  };
};

const buildBarcodeDataUrl = (code: string, height: number) => {
  const canvas = document.createElement('canvas');
  JsBarcode(canvas, code, {
    format: 'CODE128',
    displayValue: false,
    margin: 6,
    height,
    lineColor: '#000'
  });
  return canvas.toDataURL('image/png');
};

export const buildLabelHTML = async (
  order,
  labelSize: LabelSize,
  extraLines: string[] = []
): Promise<{
  html: string;
  widthIn: number;
  heightIn: number;
  widthMm: number;
  heightMm: number;
  widthPx: number;
  heightPx: number;
}> => {
  const preset = labelPresets[labelSize] || labelPresets['2x1'];
  const orderCode =
    order?.orderId ||
    buildOrderFolio(order?.folioPrefix, order?.sequence) ||
    order?._id ||
    'ORDER';

  const customerName =
    getCustomerFullName(order?.customer || order?.customerInfo) ||
    order?.customerInfo?.name ||
    order?.customer?.name;

  const lines = [customerName, ...extraLines].filter(Boolean).slice(0, 2);

  const barcodeHeightPx = preset.barcodeHeightIn * 96; // 96 DPI
  const barcodeUrl = buildBarcodeDataUrl(orderCode, barcodeHeightPx);

  const linesHtml = lines
    .map(
      (line) =>
        `<div style="font-size:${preset.fontSize}px;font-weight:600;text-align:center;line-height:1.1;margin-top:4px;word-break:break-word;">${escapeLabelHtml(
          line
        )}</div>`
    )
    .join('');

  const html = `
    <div style="width:${preset.canvasWidth};height:${preset.canvasHeight};display:flex;flex-direction:column;align-items:center;justify-content:flex-start;box-sizing:border-box;padding:16px 12px 8px 12px;font-family:Arial, sans-serif;background:#fff;color:#000;">
      <img src="${barcodeUrl}" style="max-width:100%;height:${barcodeHeightPx}px;object-fit:contain;margin-bottom:8px;" />
      ${linesHtml}
    </div>
  `;

  return {
    html,
    widthIn: preset.widthIn,
    heightIn: preset.heightIn,
    widthMm: preset.widthMm,
    heightMm: preset.heightMm,
    widthPx: preset.widthIn * 96,
    heightPx: preset.heightIn * 96
  };
};

export const wrapLabelHtmlForPrint = (
  html: string | string[],
  size?: { widthIn: number; heightIn: number }
) => {
  const labels = Array.isArray(html) ? html : [html];
  const pageStyles = size
    ? `@page{size:${size.widthIn}in ${size.heightIn}in;margin:0;}
       html,body{margin:0;padding:0;width:${size.widthIn}in;}
       .label-print-page{width:${size.widthIn}in;height:${size.heightIn}in;}`
    : 'html,body{margin:0;padding:0;}';
  return `<!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8" />
        <title>${escapeLabelHtml(t('Labels:printDocument'))}</title>
        <style>
          ${pageStyles}
          .label-print-page{break-after:page;page-break-after:always;overflow:hidden;}
          .label-print-page:last-child{break-after:auto;page-break-after:auto;}
        </style>
        <script>
          const fitLabel = ${fitLabelContent.toString()};
          async function waitForAssets() {
            await Promise.all(Array.from(document.images).map(img => img.complete ? Promise.resolve() : new Promise(resolve => { img.onload = resolve; img.onerror = resolve; })));
            if (document.fonts && document.fonts.ready) {
              try { await document.fonts.ready; } catch (e) {}
            }
            await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
          }
          async function printInsider() {
            await waitForAssets();
            document.querySelectorAll('[id="label-render-root"]').forEach(root => fitLabel(root));
            window.print();
          }
          window.addEventListener('load', printInsider, {once:true});
        </script>
      </head>
      <body style="margin:0;background:#fff;">
        ${labels.map(label => `<section class="label-print-page">${label}</section>`).join('')}
      </body>
    </html>`;
};

/** False means popup permission or document setup failed; nothing was printed. */
export const openLabelInNewTab = (html: string | string[], size?: { widthIn: number; heightIn: number }): boolean => {
  const win = window.open();
  if (!win) return false;
  try {
    win.document.write(wrapLabelHtmlForPrint(html, size));
    win.document.close();
    return true;
  } catch {
    return false;
  }
};
